/**
 * TrialBar — the single neutral bar shown to companies on a paid trial.
 *
 * Replaces the old BetaBanner ("Rektra er í lokuðu beta. Allir hlutir
 * frjálsir til notkunar..."), which contradicted the paid 14 day trial.
 *
 * Shown ONLY while `subscription_status` is trialing/trial. Hidden for
 * active (paying), exempt and grandfathered companies, and once the trial
 * has expired (the restricted banner takes over there).
 *
 * iter364: a company that chose its pakki at signup (`auto_activate`) is not
 * asked to choose again. The bar states the pakki, the price and the date the
 * subscription starts, and the only action is "Breyta".
 */
import React from 'react';
import { formatISK } from '@/utils/formatISK';
import { useNavigate } from 'react-router-dom';
import { Timer } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

export const TRIAL_STATUSES = ['trialing', 'trial'];

const isDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return y && m && d ? `${d}.${m}.${y}` : String(iso).slice(0, 10);
};

const TrialBar = ({ daysLeft, autoActivate = false, planName, monthlyIsk, firstInvoiceDate }) => {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const is = lang === 'is';
  const d = Math.max(0, daysLeft ?? 0);
  const label = is
    ? `Prufutími: ${d} ${d === 1 ? 'dagur' : 'dagar'} eftir`
    : `Trial: ${d} ${d === 1 ? 'day' : 'days'} left`;

  const showPlan = autoActivate && planName && monthlyIsk > 0 && firstInvoiceDate;
  const planLine = showPlan
    ? (is
        ? `${planName}, ${formatISK(monthlyIsk)} kr frá ${isDate(firstInvoiceDate)}`
        : `${planName}, ${formatISK(monthlyIsk)} kr from ${isDate(firstInvoiceDate)}`)
    : '';

  return (
    <div
      className="mb-3 flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2"
      data-testid="trial-bar"
      role="status"
    >
      <Timer size={15} className="text-foreground-muted flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground" data-testid="trial-bar-days">{label}</p>
        {planLine && (
          // Own line: at 390px a single line truncates the price and the date,
          // and a cut number is worse than two lines.
          <p className="text-xs text-foreground-muted" data-testid="trial-bar-plan-line">
            {planLine}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => navigate('/settings/billing')}
        className="flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-md bg-card-hover text-foreground hover:bg-border transition-colors whitespace-nowrap"
        data-testid={showPlan ? 'trial-bar-change-plan' : 'trial-bar-choose-plan'}
      >
        {showPlan ? (is ? 'Breyta' : 'Change') : (is ? 'Velja pakka' : 'Choose a plan')}
      </button>
    </div>
  );
};

export default TrialBar;
